import { useMemo } from 'react';
import { Package } from 'lucide-react';
import { useScreenData } from '@/hooks/useScreenData';
import { useScheduler } from '@/contexts/SchedulerContext';
import { ResourceRequestsTab } from '@/components/unallocated/ResourceRequestsTab';
import { DataModeToggle } from '@/components/DataModeToggle';
import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import type { PlanPreviewScenario } from '@/types/resourceRequest';

interface OpenDemandRequestsProps {
  /** Called when the user wants to preview a plan scenario on the main calendar */
  onPreviewOnCalendar?: (preview: PlanPreviewScenario) => void;
  /** When set, the plan drawer auto-opens for this request id */
  initialOpenRequestId?: string;
  /** Called once the initialOpenRequestId has been consumed so the parent can clear it */
  onInitialOpenRequestIdConsumed?: () => void;
}

export function OpenDemandRequests({
  onPreviewOnCalendar,
  initialOpenRequestId,
  onInitialOpenRequestIdConsumed,
}: OpenDemandRequestsProps) {
  const { bookings: allBookings, resources, settings, currentMode } = useScreenData('calendar');
  const { liveData } = useScheduler();
  const isLive = currentMode === 'live';

  // Bookings that are already assigned — conflict-detection context for the planner
  const allocatedBookings = useMemo(
    () => allBookings.filter(b => b.resourceId !== 'unallocated'),
    [allBookings],
  );

  const plannableResources = useMemo(() => {
    const source = isLive ? liveData.resources : resources;
    return source.filter(r => r.id !== 'unallocated');
  }, [isLive, liveData.resources, resources]);

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="w-full px-6 pt-5 pb-8 space-y-3">

        {/* ── Header ── */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
              <Package className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold leading-tight">Resource Requests</h1>
              <p className="text-muted-foreground text-sm mt-0.5">Inbound demand requests with AI-assisted planning</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <DataModeToggle screen="calendar" />
            <FeatureFeedbackCollector feature="Open Demand" />
            <HelpButton />
          </div>
        </div>

        <div data-tour="resource-requests-section">
          <ResourceRequestsTab
            resources={plannableResources}
            existingBookings={allocatedBookings}
            globalWorkingProfile={settings.globalWorkingProfile}
            businessHoursStart={settings.businessHoursStart ?? 9}
            businessHoursEnd={settings.businessHoursEnd ?? 17}
            onPreviewOnCalendar={onPreviewOnCalendar}
            initialOpenRequestId={initialOpenRequestId}
            onInitialOpenRequestIdConsumed={onInitialOpenRequestIdConsumed}
          />
        </div>
      </div>
    </div>
  );
}
